"use client";

import type { Locale } from "@mw/shared/i18n";
import type { Currency } from "@mw/shared/pricing";

import { getCurrencySymbol } from "@mw/shared/pricing";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import { logger } from "@utilities/logger";
import { useLocale } from "next-intl";
import { createContext, useContext, useEffect, useState } from "react";

interface CurrencyContextType {
  currency: Currency;
  currencySymbol: string;
  setCurrency: (currency: Currency) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

function getDefaultCurrency(locale: Locale): Currency {
  // Hindi locale defaults to INR, all others default to USD
  return locale === "hi" ? "INR" : "USD";
}

function getStoredCurrency(currentLocale: Locale): Currency | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = safeLocalStorage.getItem(LocalStorageKeys.CURRENCY);
    const storedLocale = safeLocalStorage.getItem(LocalStorageKeys.CURRENCY_LOCALE);

    // Only use stored currency if it was set for the same locale
    if (storedLocale === currentLocale && (stored === "USD" || stored === "INR")) {
      return stored;
    }
  } catch (error) {
    // LocalStorage might be unavailable
    logger.error(error instanceof Error ? error : null, "Failed to read currency from localStorage");
  }

  return null;
}

function storeCurrency(currency: Currency, locale: Locale): void {
  if (typeof window === "undefined") return;

  try {
    safeLocalStorage.setItem(LocalStorageKeys.CURRENCY, currency);
    safeLocalStorage.setItem(LocalStorageKeys.CURRENCY_LOCALE, locale);
  } catch (error) {
    // LocalStorage might be unavailable
    logger.error(error instanceof Error ? error : null, "Failed to store currency in localStorage");
  }
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const locale = useLocale() as Locale;
  // Always initialize with locale-based default to avoid hydration mismatch
  const [currency, setCurrencyState] = useState<Currency>(() => getDefaultCurrency(locale));

  const currencySymbol = getCurrencySymbol(currency);

  // After hydration, read from localStorage and update if needed
  useEffect(() => {
    const stored = getStoredCurrency(locale);
    if (stored) {
      setCurrencyState(stored);
    }
  }, [locale]);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    storeCurrency(newCurrency, locale);
  };

  return (
    <CurrencyContext.Provider value={{ currency, currencySymbol, setCurrency }}>{children}</CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextType {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}
